import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {  requestDownloadOTP, verifyAndDownload } from '../network/apis/file.api';
import DownloadOtpStyles from "../../src/styles/downloadOtp.module.css";
import Logo from '../assests/flyzone_by_flightloop.png'; 
import DefaultBackground from '../assests/public_page_bg.jpg'; 
import { getShareAssetUrls } from '../network/apis/customization.api';

const DownloadOTP = () => {
  const params = useParams();
  const decodedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => 
      [key, decodeURIComponent(value || '')]
    )
  );

  // Destructure the decoded values
  const { shareId, userId, recipientName, operationId, fileName, fileType,recipientId } = decodedParams;
  const navigate = useNavigate();
  
  // State management
  const [step, setStep] = useState(1); 
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assets, setAssets] = useState({
    bgUrl: '',
    logoUrl: ''
  });
  const [assetsLoading, setAssetsLoading] = useState(true);

  // Fetch share assets on component mount
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const result = await getShareAssetUrls(userId);
        setAssets({
          bgUrl: result.bgUrl,
          logoUrl: result.logoUrl
        });
      } catch (err) {
        // Use defaults if API fails
        setAssets({
          bgUrl: '',
          logoUrl: ''
        });
      } finally {
        setAssetsLoading(false);
      }
    };

    fetchAssets();
  }, [shareId]);

  // Handle OTP request
  const handleRequestOTP = async () => {
    try {
      setLoading(true);
      setError('');
      await requestDownloadOTP(shareId, recipientName,recipientId);
      setStep(2);
      setCountdown(60); // 60 seconds countdown
      startCountdown();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification and download
  const handleVerifyAndDownload = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Call verifyAndDownload API which returns presigned URL
      const response = await verifyAndDownload(shareId, otp,fileName,fileType,operationId);
      
      if (response.presignedUrl) {
        // Open the presigned URL in a new tab for download
        window.open(response.presignedUrl, '_blank');
        setSuccess('File downloaded successfully!');
        setStep(3); // Move to success step
      } else {
        throw new Error('No download URL received');
      }
    } catch (err) {
      if (err.message === 'The download link is invalid or the file is no longer available') {
        setError(err.message);
      } else if (err.message === 'Network error - please check your connection') {
        setError(err.message);
      } else if (err.message === 'This file has been blocked due to policy violations') {
        setError(err.message);
      } else if (err.message === 'Download failed') {
        setError('Download failed. Please try again.');
      } else if (err.message === 'No download URL received') {
        setError('Download failed - no URL received from server');
      } else {
        // Fallback for other errors (including the original OTP error handling)
        setError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer
  const startCountdown = () => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Resend OTP handler
  const handleResendOTP = async () => {
    await handleRequestOTP();
  };

  // Dynamic background style
  const backgroundStyle = {
  backgroundImage: assets.bgUrl ? `url(${assets.bgUrl})` : `url(${DefaultBackground})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  };

  return (
    <Box style={backgroundStyle}>
      {/* Watermark in bottom right corner */}


      <Box className={DownloadOtpStyles.container}>
        <Box className={DownloadOtpStyles.card}>
          {assetsLoading ? (
            <CircularProgress />
          ) : success ? (
            <>
              <Typography variant="h6" color="success.main">
                {success}
              </Typography>
            </>
          ) : (
            <>
              
                <img 
                  src={assets.logoUrl || Logo} 
                  alt="Default Logo" 
                  className={DownloadOtpStyles.logo}
                />
             
              
              <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 6 }}>
                {step === 1 ? 'Request Verification Code' : 'Enter Verification Code'}
              </Typography>

              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}

              {step === 1 ? (
                <>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    We'll send a one-time verification code to the email associated with this shared file.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleRequestOTP}
                    disabled={loading || error}
                    size="large"
                  >
                    {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Please enter the 4-digit verification code sent to your email.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter 4-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    inputProps={{ maxLength: 4 }}
                    sx={{ mb: 3 }}
                    disabled={loading || error}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleVerifyAndDownload}
                    disabled={loading || otp.length !== 4 || error}
                    size="large"
                    sx={{ mb: 2 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Verify & Download'}
                  </Button>
                  
                  {countdown > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Resend code in {countdown} seconds
                    </Typography>
                  ) : (
                    <Button
                      variant="text"
                      onClick={handleResendOTP}
                      disabled={loading}
                    >
                      Didn't receive code? Resend
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DownloadOTP;